/**
 * Topic page — modern list layout matching the Home aesthetic.
 * Renders EventCard rows with a category-coloured header pill.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Info, MessageSquare } from "lucide-react";
import { npApi } from "./api";
import EventCard from "./EventCard";
import { CATEGORY_TILES } from "./theme";

const HEALTH_TOPICS = new Set([
  "health", "medicine", "diseases", "drugs", "treatments", "clinical_trials",
]);

export default function NewsPulseTopic() {
  const { path } = useParams();
  const [events, setEvents] = useState([]);
  const [opinion, setOpinion] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let c = false;
    setLoading(true);
    Promise.all([
      npApi.byCategory(path, { limit: 50 }),
      // Companion opinion feed for this topic — visually distinct band.
      npApi.opinion({ subtopic: (path || "").split("/").pop(), limit: 4 }).catch(() => []),
    ])
      .then(([news, op]) => {
        if (c) return;
        setEvents(news || []);
        setOpinion(op || []);
      })
      .catch(() => { if (!c) setEvents([]); })
      .finally(() => { if (!c) setLoading(false); });
    return () => { c = true; };
  }, [path]);

  const label = path?.split("/").pop().replace(/_/g, " ") || path;
  const tile = CATEGORY_TILES.find((t) => t.path === path);
  const isHealth = HEALTH_TOPICS.has((path || "").split("/")[0]);

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-10" data-testid={`newspulse-topic-${path}`}>
      <div className="flex items-center gap-4 pb-4 mb-8 border-b border-slate-200">
        {tile && (
          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${tile.bg} flex items-center justify-center text-white`}>
            <span className="text-2xl">·</span>
          </div>
        )}
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Topic</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 capitalize">{label}</h1>
        </div>
      </div>

      {isHealth && (
        <div
          className="mb-8 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 flex gap-3 items-start"
          data-testid="newspulse-medical-disclaimer"
        >
          <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <p className="text-sm text-slate-700 leading-relaxed">
            <b>Medical information notice.</b> Stories in this section are for
            general informational and news purposes only and are not medical
            advice. Do not start, stop, or change any medication or treatment
            based on news coverage alone — always consult a qualified
            healthcare professional. Evidence levels shown on each card reflect
            the study type; higher levels indicate stronger evidence.
          </p>
        </div>
      )}

      {loading ? (
        <div className="space-y-4" data-testid="topic-loading">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex gap-4 animate-pulse">
              <div className="w-40 h-28 bg-slate-200 rounded-lg" />
              <div className="flex-1 py-2">
                <div className="h-3 w-24 bg-slate-200 rounded" />
                <div className="h-5 w-3/4 bg-slate-200 rounded mt-3" />
                <div className="h-4 w-1/2 bg-slate-200 rounded mt-2" />
              </div>
            </div>
          ))}
        </div>
      ) : events.length === 0 ? (
        <p className="text-slate-500 italic py-8">No events in this topic yet — new stories will appear as they're ingested.</p>
      ) : (
        <div>
          {events.map((e) => <EventCard key={e.event_id} event={e} />)}
        </div>
      )}

      {/* Companion Opinion band — visually distinct so readers see this
          is commentary, not reporting on the same topic. */}
      {opinion.length > 0 && (
        <section
          className="mt-10 rounded-2xl border-2 border-amber-300 bg-amber-50 p-6"
          data-testid="topic-opinion-band"
        >
          <div className="flex items-center justify-between pb-3 mb-4 border-b border-amber-200">
            <div className="inline-flex items-center gap-2 text-amber-900">
              <MessageSquare className="w-4 h-4" />
              <span className="text-[10px] uppercase tracking-[0.25em] font-bold">
                Opinion on {label}
              </span>
            </div>
            <Link
              to="/opinion"
              className="text-xs uppercase tracking-widest text-amber-800 hover:text-amber-900 font-semibold"
              data-testid="topic-opinion-see-all"
            >
              See all opinion →
            </Link>
          </div>
          <p className="text-xs text-amber-800 mb-4 italic">
            Views of the writers — not TheNewsPulse reporting.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {opinion.slice(0, 4).map((e) => (
              <div key={e.event_id} className="rounded-lg bg-white/70 p-3">
                <EventCard event={e} compact />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
